import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MeshBackdrop } from '@/components/MeshBackdrop'

type Clip = {
  key: string
  src: string
  poster: string
}

const CLIPS: Clip[] = [
  {
    key: 'football',
    src: '/hero-loop/football.mp4',
    poster: '/hero-loop/football-poster.jpg',
  },
  {
    key: 'basketball',
    src: '/hero-loop/basketball.mp4',
    poster: '/hero-loop/basketball-poster.jpg',
  },
  {
    key: 'padel',
    src: '/hero-loop/padel.mp4',
    poster: '/hero-loop/padel-poster.jpg',
  },
]

/** How long each sport stays on screen before crossfading to the next. */
const SLOT_MS = 7000
const FADE_MS = 1200

function usePrefersReducedMotion() {
  const [reduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })
  return reduced
}

function armVideo(el: HTMLVideoElement) {
  el.muted = true
  el.defaultMuted = true
  el.playsInline = true
  el.setAttribute('playsinline', 'true')
  el.setAttribute('webkit-playsinline', 'true')
}

/**
 * Toggles the first-paint HTML loop (if present) and keeps it muted.
 * Safe no-op when #statbox-hero-loop is not in the document.
 */
export function HeroLoopGate() {
  const { pathname } = useLocation()

  useEffect(() => {
    const onHome = pathname === '/'
    document.documentElement.classList.toggle('hide-hero-loop', !onHome)
    const video = document.getElementById('statbox-hero-loop') as HTMLVideoElement | null
    if (!video) return
    armVideo(video)
    if (onHome) {
      video.play().catch(() => {})
    } else {
      video.pause()
    }
  }, [pathname])

  return null
}

function playWhenReady(el: HTMLVideoElement, restart: boolean) {
  armVideo(el)
  const kick = () => {
    if (restart) {
      try {
        el.currentTime = 0
      } catch {
        /* iOS throws if metadata is not ready */
      }
    }
    el.play().catch(() => {})
  }
  if (el.readyState >= 2) kick()
  else el.addEventListener('loadeddata', kick, { once: true })
}

/**
 * Hero background: football → basketball → padel, crossfading every few
 * seconds. Muted + playsInline so iOS/Android will autoplay. Falls back to
 * the static aurora (MeshBackdrop) only when the user prefers reduced motion.
 */
export function HeroSportsVideo() {
  const reducedMotion = usePrefersReducedMotion()
  const [active, setActive] = useState(0)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const activeRef = useRef(0)
  activeRef.current = active

  const showVideo = !reducedMotion

  useEffect(() => {
    if (!showVideo) return
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % CLIPS.length)
    }, SLOT_MS)
    return () => window.clearInterval(id)
  }, [showVideo])

  useEffect(() => {
    if (!showVideo) return
    videoRefs.current.forEach((el, i) => {
      if (!el) return
      if (i === active) {
        playWhenReady(el, true)
      } else {
        window.setTimeout(() => {
          if (i !== activeRef.current) el.pause()
        }, FADE_MS)
      }
    })
  }, [active, showVideo])

  // iOS often blocks the first autoplay until the tab is visible or the
  // user touches the page. Retry without restarting the clip.
  useEffect(() => {
    if (!showVideo) return
    const retry = () => {
      const el = videoRefs.current[activeRef.current]
      if (el) playWhenReady(el, false)
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') retry()
    }
    document.addEventListener('touchstart', retry, { once: true, passive: true })
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('pageshow', retry)
    return () => {
      document.removeEventListener('touchstart', retry)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pageshow', retry)
    }
  }, [showVideo])

  if (!showVideo) {
    return <MeshBackdrop />
  }

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden bg-[#07080c]">
      {CLIPS.map((clip, i) => (
        <video
          key={clip.key}
          ref={(el) => {
            videoRefs.current[i] = el
            if (el) armVideo(el)
          }}
          src={clip.src}
          poster={clip.poster}
          muted
          loop
          playsInline
          autoPlay={i === 0}
          preload={i === 0 ? 'auto' : 'metadata'}
          disablePictureInPicture
          disableRemotePlayback
          className="absolute inset-0 h-full w-full object-cover motion-safe:transition-opacity"
          style={{
            opacity: i === active ? 1 : 0,
            transitionDuration: `${FADE_MS}ms`,
          }}
        />
      ))}

      {/* Dark scrim — keeps the whole frame moody instead of raw stock */}
      <div className="absolute inset-0 bg-black/55" />

      {/* Left-side legibility gradient behind the copy on desktop */}
      <div
        className="absolute inset-0 hidden lg:block"
        style={{
          background:
            'linear-gradient(to right, rgba(5,6,10,0.85) 0%, rgba(5,6,10,0.45) 48%, rgba(5,6,10,0.15) 100%)',
        }}
      />
      {/* Mobile: darken center-bottom where the copy stacks */}
      <div
        className="absolute inset-0 lg:hidden"
        style={{
          background:
            'linear-gradient(to bottom, rgba(5,6,10,0.55) 0%, rgba(5,6,10,0.72) 55%, rgba(5,6,10,0.85) 100%)',
        }}
      />

      {/* Brand teal grade over the footage */}
      <div
        className="absolute inset-0 mix-blend-soft-light"
        style={{
          background:
            'radial-gradient(ellipse 90% 60% at 50% 0%, rgba(113,205,217,0.55), transparent 65%)',
        }}
      />
      <div className="hero-aurora-horizon absolute inset-0 opacity-70" />

      {/* Grain, same texture as the rest of the site */}
      <div
        className="hero-grain absolute inset-0 mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          backgroundSize: '180px 180px',
        }}
      />
    </div>
  )
}
